/**
 * Centralised date formatting for the web app.
 *
 * The app's UI language is English, but `toLocaleDateString(undefined, …)`
 * falls back to the *browser's* locale — so a user with a Swedish browser
 * would see "25 maj 2026" instead of "25 May 2026". To keep dates consistent
 * with the chosen UI language we pin every display date to a fixed locale.
 *
 * Day-month-year ordering ("25 May 2026") is kept to match what family
 * members are already used to seeing.
 */
export const APP_LOCALE = "en-GB";

/** Short date: "25 May 2026". The default for most timestamps. */
export function formatDate(value: string | number | Date): string {
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString(APP_LOCALE, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/** Long date: "25 May 2026" with the full month name. */
export function formatDateLong(value: string | number | Date): string {
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString(APP_LOCALE, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

/** Date + time, English locale. */
export function formatDateTime(value: string | number | Date): string {
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString(APP_LOCALE, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}
